import {
  getRemoteControlSnapshot,
  listRemoteControlEvents,
  listRemoteControlSteps,
  type RemoteControlStep,
} from '@web/api/remoteControl';
import {
  completeProjectViewResync,
  importLegacyChatSteps,
  projectRawEvents,
  projectSnapshot,
  type ProjectViewState,
  type ProjectedLegacyStep,
} from '@web/lib/projector';
import { RunEventIngress, runProjectionStore } from '@web/lib/runEvents';
import { useCallback, useMemo, useRef, useState } from 'react';

const HISTORY_PAGE_SIZE = 1000;
const SNAPSHOT_EVENT_LIMIT = 5000;
const LEGACY_HISTORY_LIMIT = 5000;
const MAX_STEPS_IN_MEMORY = 500;
const MAX_BUFFERED_EVENTS = 2000;

/**
 * A delta request is meaningful only after a bounded canonical snapshot has
 * established the current cursor. Starting at zero would download the entire
 * project history just to reconstruct state the snapshot endpoint already
 * provides.
 */
export function deltaSyncRequiresSnapshot(
  view: ProjectViewState | null
): boolean {
  return !view || view.currentCursor <= 0;
}

function remoteControlStep(step: ProjectedLegacyStep): RemoteControlStep {
  return {
    step_id: step.stepId,
    task_id: step.taskId,
    project_id: step.projectId,
    step: step.step,
    data: step.data,
    timestamp: step.timestamp,
  };
}

function visibleSteps(view: ProjectViewState | null): RemoteControlStep[] {
  if (!view) return [];
  const steps = view.legacySteps.map(remoteControlStep);
  return steps.length > MAX_STEPS_IN_MEMORY
    ? steps.slice(steps.length - MAX_STEPS_IN_MEMORY)
    : steps;
}

export function useRemoteHistory(sessionId: string, token: string) {
  const projectorRef = useRef<ProjectViewState | null>(null);
  const ingressRef = useRef(new RunEventIngress());
  const projectIdRef = useRef<string | null>(null);
  const nextLegacySinceRef = useRef(0);
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  const generationRef = useRef(0);
  const pendingCanonicalRef = useRef<unknown[]>([]);
  const pendingLegacyRef = useRef<unknown[]>([]);
  const legacyFallbackRef = useRef(false);
  const [projectView, setProjectView] = useState<ProjectViewState | null>(null);

  const publish = useCallback((next: ProjectViewState) => {
    if (projectIdRef.current !== next.projectId) return;
    runProjectionStore.replaceProject(next);
    projectorRef.current = next;
    setProjectView(next);
  }, []);

  const reset = useCallback((projectId: string | null) => {
    generationRef.current += 1;
    const previousProjectId = projectIdRef.current;
    projectIdRef.current = projectId;
    projectorRef.current = null;
    ingressRef.current = new RunEventIngress();
    if (previousProjectId) runProjectionStore.clearProject(previousProjectId);
    nextLegacySinceRef.current = 0;
    syncInFlightRef.current = null;
    pendingCanonicalRef.current = [];
    pendingLegacyRef.current = [];
    legacyFallbackRef.current = false;
    setProjectView(null);
  }, []);

  const applyCanonicalEvents = useCallback(
    (
      events: unknown[],
      deliveryMode: 'live' | 'reconnect_catch_up' = 'live'
    ): boolean => {
      const current = projectorRef.current;
      if (!events.length) return false;
      if (!current) {
        pendingCanonicalRef.current = [
          ...pendingCanonicalRef.current,
          ...events,
        ].slice(-MAX_BUFFERED_EVENTS);
        return false;
      }
      let next = current;
      for (const event of events) {
        next = ingressRef.current.apply(next, event, deliveryMode);
      }
      projectorRef.current = next;
      setProjectView(next);
      return next.needsResync;
    },
    []
  );

  const applyLegacyEvents = useCallback(
    (events: unknown[]) => {
      const current = projectorRef.current;
      if (!events.length) return;
      if (!current) {
        pendingLegacyRef.current = [
          ...pendingLegacyRef.current,
          ...events,
        ].slice(-MAX_BUFFERED_EVENTS);
        return;
      }
      const next = projectRawEvents(
        current.projectId,
        importLegacyChatSteps(events),
        'live',
        { ...current, mode: 'live' }
      );
      publish(next);
    },
    [publish]
  );

  const flushBufferedEvents = useCallback(() => {
    const current = projectorRef.current;
    if (!current) return;

    const canonical = pendingCanonicalRef.current;
    const legacy = pendingLegacyRef.current;
    pendingCanonicalRef.current = [];
    pendingLegacyRef.current = [];

    if (canonical.length) {
      canonical.sort((left, right) => {
        const leftCursor = Number(
          (left as Record<string, unknown> | null)?.cloud_cursor || 0
        );
        const rightCursor = Number(
          (right as Record<string, unknown> | null)?.cloud_cursor || 0
        );
        return leftCursor - rightCursor;
      });
      applyCanonicalEvents(canonical);
    }
    if (legacy.length) applyLegacyEvents(legacy);
  }, [applyCanonicalEvents, applyLegacyEvents]);

  const fetchLegacyPages = useCallback(
    async (projectId: string, startSince: number) => {
      const items: RemoteControlStep[] = [];
      let since = startSince;
      let hasMore = false;
      let pageCount = 0;
      const maxPages = Math.ceil(LEGACY_HISTORY_LIMIT / HISTORY_PAGE_SIZE) + 1;
      while (items.length < LEGACY_HISTORY_LIMIT && pageCount < maxPages) {
        pageCount += 1;
        const page = await listRemoteControlSteps(
          sessionId,
          token,
          projectId,
          since,
          Math.min(HISTORY_PAGE_SIZE, LEGACY_HISTORY_LIMIT - items.length)
        );
        items.push(...(page.items || []));
        hasMore = page.has_more;
        if (!hasMore || page.next_since <= since) {
          since = Math.max(since, page.next_since || since);
          break;
        }
        since = page.next_since;
      }
      return { items, hasMore, nextSince: since };
    },
    [sessionId, token]
  );

  const rehydrateSnapshot = useCallback(
    async (projectId = projectIdRef.current) => {
      if (!projectId) return;
      const generation = generationRef.current;
      const snapshot = await getRemoteControlSnapshot(
        sessionId,
        token,
        projectId,
        SNAPSHOT_EVENT_LIMIT
      );
      if (
        generation !== generationRef.current ||
        projectIdRef.current !== projectId
      ) {
        return;
      }
      publish({
        ...projectSnapshot(snapshot, projectorRef.current),
        lastSyncedAt: new Date().toISOString(),
      });
      flushBufferedEvents();
    },
    [flushBufferedEvents, publish, sessionId, token]
  );

  const syncDeltas = useCallback(async () => {
    if (syncInFlightRef.current) return syncInFlightRef.current;
    const projectId = projectIdRef.current;
    const generation = generationRef.current;
    if (!projectId) return;

    const operation = (async () => {
      if (deltaSyncRequiresSnapshot(projectorRef.current)) {
        await rehydrateSnapshot(projectId);
        return;
      }
      let cursor = projectorRef.current?.currentCursor || 0;
      let authoritativeCursor = cursor;
      for (;;) {
        const page = await listRemoteControlEvents(
          sessionId,
          token,
          projectId,
          cursor,
          HISTORY_PAGE_SIZE
        );
        if (
          generation !== generationRef.current ||
          projectIdRef.current !== projectId
        ) {
          return;
        }
        applyCanonicalEvents(page.items || [], 'reconnect_catch_up');
        if (page.has_more && page.next_cursor <= cursor) {
          await rehydrateSnapshot(projectId);
          return;
        }
        cursor = page.next_cursor;
        authoritativeCursor = page.current_cursor;
        if (!page.has_more) break;
      }

      const view = projectorRef.current;
      if (!view?.needsResync) return;
      const recovered = completeProjectViewResync(view, authoritativeCursor);
      if (recovered === view) {
        await rehydrateSnapshot(projectId);
      } else {
        publish(recovered);
      }
    })().finally(() => {
      if (generation === generationRef.current) syncInFlightRef.current = null;
    });
    syncInFlightRef.current = operation;
    return operation;
  }, [applyCanonicalEvents, publish, rehydrateSnapshot, sessionId, token]);

  const syncLegacySteps = useCallback(async () => {
    const projectId = projectIdRef.current;
    const generation = generationRef.current;
    if (!projectId) return;
    const since = nextLegacySinceRef.current;
    const history = await fetchLegacyPages(projectId, since);
    if (
      generation !== generationRef.current ||
      projectIdRef.current !== projectId
    ) {
      return;
    }
    applyLegacyEvents(history.items);
    nextLegacySinceRef.current = Math.max(since, history.nextSince);
  }, [applyLegacyEvents, fetchLegacyPages]);

  const hydrate = useCallback(
    async (projectId: string) => {
      reset(projectId);
      const generation = generationRef.current;
      let snapshot: Awaited<
        ReturnType<typeof getRemoteControlSnapshot>
      > | null = null;
      try {
        snapshot = await getRemoteControlSnapshot(
          sessionId,
          token,
          projectId,
          SNAPSHOT_EVENT_LIMIT
        );
      } catch (error) {
        // During a rolling Server deployment the canonical endpoints may not
        // exist yet. Fall back to bounded legacy ChatStep playback.
        console.warn(
          '[RemoteHistory] canonical snapshot unavailable; using legacy history',
          error
        );
      }
      if (
        generation !== generationRef.current ||
        projectIdRef.current !== projectId
      ) {
        return;
      }
      if (snapshot) {
        const hasCanonicalHistory =
          snapshot.current_cursor > 0 ||
          snapshot.recent_events.length > 0 ||
          (snapshot.runs?.length || 0) > 0;
        if (hasCanonicalHistory) {
          // Projection/contract errors are not transport unavailability. Let
          // them surface without overwriting a canonical view with legacy
          // ChatStep data.
          const canonical = projectSnapshot(snapshot);
          legacyFallbackRef.current = false;
          publish({
            ...canonical,
            eventsTruncated: snapshot.events_truncated,
            lastSyncedAt: new Date().toISOString(),
          });
          flushBufferedEvents();
          return;
        }
      }
      const history = await fetchLegacyPages(projectId, 0);
      if (
        generation !== generationRef.current ||
        projectIdRef.current !== projectId
      ) {
        return;
      }
      legacyFallbackRef.current = true;
      nextLegacySinceRef.current = history.nextSince;
      const hydrated = {
        ...projectRawEvents(
          projectId,
          importLegacyChatSteps(history.items),
          'rehydrate'
        ),
        eventsTruncated: history.hasMore,
        lastSyncedAt: new Date().toISOString(),
      };
      if (
        generation !== generationRef.current ||
        projectIdRef.current !== projectId
      ) {
        return;
      }
      publish(hydrated);
      flushBufferedEvents();
    },
    [fetchLegacyPages, flushBufferedEvents, publish, reset, sessionId, token]
  );

  const recordLegacyFrame = useCallback(
    (frame: Record<string, unknown>) => {
      applyLegacyEvents([frame]);
      if (typeof frame.step_id === 'number') {
        nextLegacySinceRef.current = Math.max(
          nextLegacySinceRef.current,
          frame.step_id
        );
      }
    },
    [applyLegacyEvents]
  );

  const ensureCursor = useCallback(
    (cursor: unknown) => {
      if (
        typeof cursor === 'number' &&
        cursor > (projectorRef.current?.currentCursor || 0)
      ) {
        if ((projectorRef.current?.currentCursor || 0) === 0) {
          return rehydrateSnapshot(projectIdRef.current);
        }
        return syncDeltas();
      }
      return undefined;
    },
    [rehydrateSnapshot, syncDeltas]
  );
  const needsLegacySync = useCallback(() => legacyFallbackRef.current, []);

  return {
    projectView,
    steps: useMemo(() => visibleSteps(projectView), [projectView]),
    hydrate,
    reset,
    syncDeltas,
    syncLegacySteps,
    rehydrateSnapshot,
    applyCanonicalEvents,
    recordLegacyFrame,
    ensureCursor,
    currentCursor: () => projectorRef.current?.currentCursor || 0,
    currentProjectId: () => projectIdRef.current,
    needsLegacySync,
  };
}
